<template>
  <v-container fluid class="account-settings-page">
    <v-row>
      <!-- Kolom kiri: identitas + form -->
      <v-col cols="12" md="7">
        <v-card ref="profileCard" class="rounded-lg elevation-2 profile-card">
          <div class="profile-banner"></div>
          <v-card-text class="profile-header pa-6 pt-0">
            <div class="avatar-upload">
              <v-avatar size="84" color="grey lighten-3" class="profile-avatar">
                <v-img :src="avatarPreview" @error="avatarLoadFailed = true" />
              </v-avatar>
              <v-btn
                fab
                x-small
                color="primary"
                class="avatar-upload__btn"
                @click="$refs.fileInput.click()"
              >
                <v-icon small>mdi-camera</v-icon>
              </v-btn>
              <input
                ref="fileInput"
                type="file"
                accept="image/jpeg,image/png"
                class="d-none"
                @change="onFileChange"
              />
            </div>

            <div class="d-flex align-center flex-wrap mt-3" style="gap:10px;">
              <div>
                <div class="d-flex align-center" style="gap:8px;">
                  <div class="text-h6 font-weight-bold">{{ user?.name }}</div>
                  <v-chip x-small color="primary" outlined>
                    <v-icon x-small left>mdi-shield-account-outline</v-icon>
                    {{ roleLabel }}
                  </v-chip>
                </div>
                <div class="grey--text text--darken-1 text-body-2">{{ user?.email }}</div>
              </div>
              <v-spacer></v-spacer>
              <v-btn outlined small color="grey darken-1" @click="goToChangePassword">
                <v-icon left small>mdi-lock-reset</v-icon>
                Ganti Password
              </v-btn>
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-text class="pt-5">
            <v-form ref="form" @submit.prevent="submit">
              <v-text-field
                :value="user?.name"
                label="Nama"
                prepend-inner-icon="mdi-account-outline"
                outlined
                dense
                disabled
                class="mb-1"
              ></v-text-field>
              <v-text-field
                :value="user?.email"
                label="Email"
                prepend-inner-icon="mdi-email-outline"
                outlined
                dense
                disabled
                class="mb-1"
              ></v-text-field>
              <v-text-field
                v-model="form.phone"
                label="Nomor HP"
                prepend-inner-icon="mdi-phone-outline"
                outlined
                dense
                :rules="[v => !!v || 'Nomor HP wajib diisi']"
              ></v-text-field>
              <div class="caption grey--text mb-4">
                <v-icon x-small color="grey">mdi-information-outline</v-icon>
                Nama &amp; email terkunci — hubungi admin bila perlu diubah.
              </div>
              <v-btn
                color="primary"
                :loading="saving"
                :disabled="!isDirty"
                type="submit"
                large
                style="border-radius: 8px; font-weight: 600;"
                class="white--text"
              >
                <v-icon left>mdi-content-save-outline</v-icon>
                Simpan Perubahan
              </v-btn>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <!-- Info tambahan biar padat, semua readonly -->
          <v-card-text class="py-4">
            <v-row dense>
              <v-col cols="4" v-for="info in extraInfo" :key="info.label">
                <div class="info-tile">
                  <v-icon small color="primary" class="mb-1">{{ info.icon }}</v-icon>
                  <div class="info-tile__label">{{ info.label }}</div>
                  <div class="info-tile__value">{{ info.value }}</div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Kolom kanan: aktivitas — tingginya menyamai kolom kiri lewat JS
           (diukur di syncActivityCardHeight), lebih pasti daripada
           mengandalkan flex/grid stretch yang gampang meleset di Vuetify. -->
      <v-col cols="12" md="5">
        <v-card class="rounded-lg elevation-2 activity-card" :style="activityCardStyle">
          <v-card-title class="text-subtitle-1 font-weight-medium">
            <v-icon color="primary" class="mr-2">mdi-history</v-icon>
            Aktivitas Saya
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-2 activity-scroll">
            <div v-if="loadingLogs" class="text-center py-6">
              <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
            </div>
            <div v-else-if="!logs.length" class="text-center grey--text py-6">
              <v-icon color="grey lighten-1" size="32">mdi-timeline-text-outline</v-icon>
              <div class="mt-2 caption">Belum ada aktivitas tercatat.</div>
            </div>
            <v-list v-else dense class="py-0">
              <v-list-item v-for="log in logs" :key="log.id" class="px-0 activity-item">
                <v-list-item-icon class="mr-3">
                  <v-avatar size="28" :color="eventChip(log.event).bg">
                    <v-icon x-small :color="eventChip(log.event).color">{{ eventChip(log.event).icon }}</v-icon>
                  </v-avatar>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ log.description }}</v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    {{ log.log_label }} &middot; {{ formatRelative(log.created_at) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" :timeout="snackbar.timeout" top>
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: 'AccountSettings',
  data() {
    return {
      form: {
        phone: '',
        profile_img: null,
      },
      localPreviewUrl: null,
      avatarLoadFailed: false,
      saving: false,
      logs: [],
      loadingLogs: false,
      activityCardHeight: null,
      snackbar: { visible: false, color: 'success', text: '', timeout: 4000 },
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    roleLabel() {
      const labels = { super_admin: 'Super Admin', admin: 'Admin', user: 'User' };
      return labels[this.user?.role] || this.user?.role || '';
    },
    avatarPreview() {
      if (this.localPreviewUrl) return this.localPreviewUrl;
      const filename = this.user?.image;
      return (filename && !this.avatarLoadFailed)
        ? `${process.env.VUE_APP_URL}/storage/${filename}`
        : 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png';
    },
    isDirty() {
      return !!this.form.profile_img || this.form.phone !== (this.user?.phone || '');
    },
    activityCardStyle() {
      return this.activityCardHeight ? { maxHeight: `${this.activityCardHeight}px` } : {};
    },
    extraInfo() {
      const entityName = this.$store.state.entity?.name || '-';
      return [
        { label: 'Entity', icon: 'mdi-domain', value: entityName },
        { label: 'Bergabung', icon: 'mdi-calendar-account-outline', value: this.formatDate(this.user?.created_at) },
        { label: 'Login Terakhir', icon: 'mdi-clock-outline', value: this.user?.last_login_at ? this.formatDate(this.user.last_login_at) : '-' },
      ];
    },
  },
  mounted() {
    this.form.phone = this.user?.phone || '';
    this.fetchMyLogs();
    this.syncActivityCardHeight();
    window.addEventListener('resize', this.syncActivityCardHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.syncActivityCardHeight);
    if (this.localPreviewUrl) URL.revokeObjectURL(this.localPreviewUrl);
  },
  methods: {
    // Vuetify grid tidak konsisten menyamakan tinggi 2 kolom lewat CSS stretch
    // saja (max-height dari salah satu kartu bisa "dianggap" lebih dulu oleh
    // browser sebelum di-stretch) — ukur langsung tinggi kartu kiri dan pakai
    // itu sebagai batas kartu kanan. Hanya berlaku di layar >= md (side-by-side);
    // di mobile keduanya ditumpuk jadi tidak perlu dibatasi.
    syncActivityCardHeight() {
      this.$nextTick(() => {
        if (this.$vuetify.breakpoint.mdAndUp && this.$refs.profileCard) {
          this.activityCardHeight = this.$refs.profileCard.$el.offsetHeight;
        } else {
          this.activityCardHeight = null;
        }
      });
    },

    goToChangePassword() {
      this.$router.push('/change-password').catch(() => {});
    },

    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.form.profile_img = file;
      this.avatarLoadFailed = false;
      if (this.localPreviewUrl) URL.revokeObjectURL(this.localPreviewUrl);
      this.localPreviewUrl = URL.createObjectURL(file);
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      const formData = new FormData();
      formData.append('phone', this.form.phone);
      if (this.form.profile_img) {
        formData.append('profile_img', this.form.profile_img);
      }

      try {
        const res = await this.$axios.post(`${process.env.VUE_APP_URL}/api/profile`, formData, {
          headers: {
            Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        this.$store.commit('SET_USER', res.data.user);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        this.form.profile_img = null;
        if (this.localPreviewUrl) {
          URL.revokeObjectURL(this.localPreviewUrl);
          this.localPreviewUrl = null;
        }

        this.snackbar = { visible: true, color: 'success', text: 'Profil berhasil diperbarui.', timeout: 3000 };
        this.fetchMyLogs();
      } catch (err) {
        const errors = err.response?.data?.errors;
        const message = errors ? Object.values(errors)[0][0] : (err.response?.data?.message || 'Gagal memperbarui profil.');
        this.snackbar = { visible: true, color: 'error', text: message, timeout: 5000 };
      } finally {
        this.saving = false;
      }
    },

    async fetchMyLogs() {
      this.loadingLogs = true;
      try {
        const res = await this.$axios.get(`${process.env.VUE_APP_URL}/api/my-activity-logs`, {
          headers: { Authorization: `Bearer ${this.$store.getters.isTokenUser}` },
        });
        this.logs = res.data;
      } catch (err) {
        // feed pengisi halaman, gagal diam-diam saja
      } finally {
        this.loadingLogs = false;
      }
    },

    eventChip(event) {
      const map = {
        created: { color: '#16A34A', bg: 'green lighten-5', icon: 'mdi-plus' },
        updated: { color: '#D97706', bg: 'orange lighten-5', icon: 'mdi-pencil' },
        deleted: { color: '#DC2626', bg: 'red lighten-5', icon: 'mdi-delete' },
        login: { color: '#0891B2', bg: 'cyan lighten-5', icon: 'mdi-login' },
        logout: { color: '#64748B', bg: 'grey lighten-3', icon: 'mdi-logout' },
        batch_import: { color: '#0891B2', bg: 'cyan lighten-5', icon: 'mdi-file-import' },
      };
      return map[event] || { color: '#64748B', bg: 'grey lighten-3', icon: 'mdi-circle-small' };
    },

    formatRelative(value) {
      const diffMs = Date.now() - new Date(value).getTime();
      const minutes = Math.floor(diffMs / 60000);
      if (minutes < 1) return 'Baru saja';
      if (minutes < 60) return `${minutes} menit lalu`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} jam lalu`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days} hari lalu`;
      return this.formatDate(value);
    },

    formatDate(value) {
      if (!value) return '-';
      return new Date(value).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
.account-settings-page {
  padding-top: 8px;
}

.profile-banner {
  height: 56px;
  background: linear-gradient(135deg, #2d4f6c 0%, #0891B2 100%);
  border-radius: 8px 8px 0 0;
}
.profile-header {
  margin-top: -36px;
}
.avatar-upload {
  position: relative;
  display: inline-block;
}
.profile-avatar {
  border: 4px solid #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,.15);
}
.avatar-upload__btn {
  position: absolute;
  bottom: -2px;
  right: -2px;
  box-shadow: 0 2px 6px rgba(0,0,0,.25);
}

.info-tile {
  background: #F8FAFC;
  border: 1px solid #F1F5F9;
  border-radius: 8px;
  padding: 10px 12px;
  height: 100%;
}
.info-tile__label {
  font-size: 10.5px;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: .3px;
}
.info-tile__value {
  font-size: 13px;
  font-weight: 500;
  color: #1E293B;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.activity-card {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.activity-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.activity-item + .activity-item {
  border-top: 1px solid #F1F5F9;
}
</style>
